import { useEffect, useState, type FormEvent } from 'react';
import { LEARNABLE_SKILLS, SKILL_NAMES, SKILLS, type MeDto, type SkillId } from '@setu/shared';
import { api, ApiError, type InvitePreview } from '../api.ts';

/**
 * The hours options the team settled on: ten minutes a day is a real answer,
 * and the steps above it are whole hours rather than the old "3–5 hrs" range
 * that no chip ever matched. Exported so the profile editor offers the same
 * answers this form did.
 */
export const HOURS = [
  { minutes: 70, label: '10 min a day' },
  { minutes: 60, label: '1 hr a week' },
  { minutes: 120, label: '2 hrs a week' },
  { minutes: 180, label: '3 hrs a week' },
  { minutes: 300, label: '5 hrs a week' },
  { minutes: 600, label: '10 hrs a week' },
];

export default function AcceptInvite({
  token,
  onAccepted,
}: {
  token: string;
  onAccepted: (user: MeDto) => void;
}): React.ReactElement {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [skills, setSkills] = useState<SkillId[]>([]);
  const [otherSkills, setOtherSkills] = useState('');
  const [learn, setLearn] = useState<SkillId[]>([]);
  const [weeklyMinutes, setWeeklyMinutes] = useState(180);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .invitePreview(token)
      .then(({ invite }) => {
        if (cancelled) return;
        setPreview(invite);
        setName(invite.name ?? '');
        // Whatever the coordinator already listed for them arrives ticked.
        setSkills(invite.skills as SkillId[]);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'That link did not work.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const toggle = (list: SkillId[], set: (next: SkillId[]) => void, skill: SkillId) => {
    set(list.includes(skill) ? list.filter((s) => s !== skill) : [...list, skill]);
  };

  // The server enforces both of these too — this just keeps the button
  // honest about whether pressing it could possibly work.
  const ready = name.trim().length > 0 && password.length >= 10;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const { user } = await api.acceptInvite({ token, name, password, skills, otherSkills, learn, weeklyMinutes });
      onAccepted(user);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="gate">
        <div className="card stack">
          <h1>That link has expired.</h1>
          <p className="soft">{loadError}</p>
          <a className="btn" href="/">
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="gate">
        <p className="muted">Opening your invitation…</p>
      </div>
    );
  }

  return (
    <div className="gate">
      <div className="card stack-lg" style={{ maxWidth: 520 }}>
        <div>
          <div className="brand" style={{ paddingLeft: 0 }}>
            <b>Setu</b>
            <span>the bridge</span>
          </div>
          <h1 style={{ marginTop: 14 }}>You've been invited.</h1>
          <p className="soft" style={{ marginTop: 6 }}>
            {preview.cityName
              ? `As a volunteer in ${preview.cityName}. Two minutes and you're in.`
              : 'Two minutes and you are in.'}
          </p>
        </div>

        <form className="stack-lg" onSubmit={submit}>
          <div className="stack">
            <div className="field">
              <label htmlFor="email">Email</label>
              {/* Fixed by the invitation — changing it would make the invite meaningless. */}
              <input id="email" type="email" value={preview.email} readOnly disabled />
              <span className="hint">This is the address your coordinator invited.</span>
            </div>

            <div className="field">
              <label htmlFor="name">Your name</label>
              <input
                id="name"
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={Boolean(fields.name)}
                aria-describedby="name-hint"
              />
              <span className="hint" id="name-hint">
                The name your team will know you by.
              </span>
              {fields.name && <span className="error">{fields.name}</span>}
            </div>

            <div className="field">
              <label htmlFor="password">Pick a password</label>
              <input
                id="password"
                type="password"
                required
                minLength={10}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={Boolean(fields.password)}
                aria-describedby="password-hint"
              />
              <span className="hint" id="password-hint">
                At least 10 characters.
              </span>
              {fields.password && <span className="error">{fields.password}</span>}
            </div>
          </div>

          <div className="stack">
            <div>
              <h2>What can you help with?</h2>
              <p className="small muted">Pick as many as fit. It decides what gets shown to you.</p>
            </div>
            <div className="chips">
              {SKILLS.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  className="chip"
                  aria-pressed={skills.includes(skill)}
                  onClick={() => toggle(skills, setSkills, skill)}
                >
                  {SKILL_NAMES[skill]}
                </button>
              ))}
            </div>
            <div className="field">
              <label htmlFor="other-skills">Other</label>
              <input
                id="other-skills"
                type="text"
                maxLength={200}
                value={otherSkills}
                onChange={(e) => setOtherSkills(e.target.value)}
              />
              <span className="hint">Something you're good at that isn't listed.</span>
            </div>
          </div>

          <div className="stack">
            <div>
              <h2>Anything you'd like to learn?</h2>
              <p className="small muted">Optional. We'll point you at the playbooks and pair you up.</p>
            </div>
            <div className="chips">
              {LEARNABLE_SKILLS.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  className="chip"
                  aria-pressed={learn.includes(skill)}
                  onClick={() => toggle(learn, setLearn, skill)}
                >
                  {SKILL_NAMES[skill]}
                </button>
              ))}
            </div>
          </div>

          <div className="stack">
            <div>
              <h2>Honestly, how much time?</h2>
              <p className="small muted">
                Be honest rather than generous — nothing longer than this will be put in front of you.
              </p>
            </div>
            <div className="chips">
              {HOURS.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className="chip"
                  aria-pressed={weeklyMinutes === option.minutes}
                  onClick={() => setWeeklyMinutes(option.minutes)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <p className="notice alert" role="alert">
              {error}
            </p>
          )}

          <button className="btn block" type="submit" disabled={busy || !ready}>
            {busy ? 'Setting you up…' : 'Join Setu'}
          </button>
        </form>
      </div>
    </div>
  );
}
